import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Dropdown, Menu, message } from 'antd';
import { ReactComponent as UpArrow } from '../../icons/sort_asc.svg';
import { ReactComponent as DownArrow } from '../../icons/sort_desc.svg';
import { ReactComponent as MoreIcon } from '../../icons/more-icon.svg';
import { ReactComponent as EyeIcon } from '../../icons/eye.svg';
import { ReactComponent as EditIcon } from '../../icons/edit-icon.svg';
import { ReactComponent as MessageIcon } from '../../icons/messages.svg';
import NameImage from '../client/NameImage';
import { getRandomColor } from '../../constants/palette';
import useHirePortTheme from '../../hooks/useHirePortTheme';
import { JOBS, RECRUITER } from '../../constants/routes';
import TooltipPrompt from '../../UI/tootlip';
import { JobAssignment, PaginationInfo } from '../../hooks/useJobAssignments';

// Match the exact styling from RecruiterManagementTable.tsx
const JobAssignmentsTableDiv = styled.div`
  padding: 0 2rem;
  background: #ffffff;
  border-radius: ${({ theme }) => theme.radius};
  @media (max-width: 576px) {
    padding: 0 1rem;
  }
`;

// Match the exact table header styling from RecruiterSelectTableHeader.tsx
const TableHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  border-bottom: 1px solid #DAE5EC;
  padding: 12px 0;
  color: #627D95;
  font-weight: 600;
  font-size: 0.75rem;
  line-height: 1.33;
  letter-spacing: 0.8px;
`;

const TableHeaderCell = styled.div<{ flex?: string; textAlign?: string }>`
  flex: ${props => props.flex || '1'};
  text-align: ${props => props.textAlign || 'left'};
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.33;
  letter-spacing: 0.8px;
  color: #627D95;
`;

const HoverDiv = styled.div`
  &:hover {
    cursor: pointer;
  }
`;

const StyledUpArrow = styled(UpArrow)`
  & path {
    stroke: ${(props) => props.color};
  }
`;

const StyledDownArrow = styled(DownArrow)`
  & path {
    stroke: ${(props) => props.color};
  }
`;

const TableRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  padding: 1rem 0;
  border-bottom: 1px solid #dae5ec;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div<{ flex?: string; clickable?: boolean }>`
  flex: ${props => props.flex || '1'};
  padding: 0 0.5rem;
  font-size: 0.875rem;
  line-height: 1.43;
  color: #061c2e;
  
  ${props => props.clickable && `
    cursor: pointer;
    &:hover {
      color: ${props.theme?.primaryColor || '#34ce72'};
    }
  `}
`;

const JobTitle = styled.span`
  font-weight: 600;
  cursor: pointer;
  color: #061c2e;
  
  &:hover {
    color: ${({ theme }) => theme.primaryColor};
  }
`;

const CompanyName = styled.span`
  font-size: 0.75rem;
  color: #627d95;
  display: block;
  margin-top: 0.25rem;
`;

const StatusBadge = styled.span<{ active: boolean }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => props.active ? '#eaf7ef' : '#f2f6fa'};
  color: ${props => props.active ? '#29ae5e' : '#627d95'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
`;

const FeeType = styled.span`
  font-size: 0.75rem;
  color: #627d95;
  text-transform: capitalize;
`;

const DateText = styled.span`
  font-size: 0.75rem;
  color: #627d95;
`;

const TargetText = styled.span`
  font-size: 0.75rem;
  color: #061c2e;
  font-weight: 500;
`;

const TargetType = styled.span`
  font-size: 0.625rem;
  color: #627d95;
  display: block;
  text-transform: capitalize;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 1.5rem 0;
  border-top: 1px solid #dae5ec;
`;

const PaginationButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  padding: 0.5rem 0.75rem;
  margin: 0 0.25rem;
  border: 1px solid #dae5ec;
  background-color: ${props => props.active ? props.theme?.primaryColor : '#ffffff'};
  color: ${props => props.active ? '#ffffff' : '#061c2e'};
  border-radius: ${({ theme }) => theme.radius};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.active ? props.theme?.hoverColor : props.theme?.lozengeColor};
  }
  
  &:disabled {
    opacity: 0.5;
  }
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TableActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${({ theme }) => theme.radius};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${({ theme }) => theme.lozengeColor};
  }
  
  & svg {
    width: 1rem;
    height: 1rem;
    color: #627d95;
  }
  
  &:hover svg {
    color: ${({ theme }) => theme.primaryColor};
  }
`;



interface JobAssignmentsTableProps {
    assignments: JobAssignment[];
    pagination: PaginationInfo | null;
    onSort: (sortField: string) => void;
    onPageChange: (page: number) => void;
    currentSort: string;
}

const TableHeaderName = ({
    name,
    sortField,
    currentSort,
    onSort,
}: {
    name: string;
    sortField: string;
    currentSort: string;
    onSort: (sortField: string) => void;
}) => {
    const theme = useHirePortTheme();
    const isCurrentSort = currentSort === sortField || currentSort === `-${sortField}`;
    const isDesc = currentSort === `-${sortField}`;

    let upArrowColor = '#627d95';
    let downArrowColor = '#627d95';

    if (isCurrentSort) {
        if (isDesc) {
            downArrowColor = theme.primaryColor;
            upArrowColor = '#627d95';
        } else {
            upArrowColor = theme.primaryColor;
            downArrowColor = '#627d95';
        }
    }

    return (
        <HoverDiv
            style={{ display: 'flex', alignItems: 'center' }}
            onClick={() => onSort(sortField)}
        >
            <span
                style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginRight: '0.5rem'
                }}
            >
                <StyledDownArrow
                    color={downArrowColor}
                    style={{ marginBottom: '-9px' }}
                />
                <StyledUpArrow color={upArrowColor} />
            </span>
            {name}
        </HoverDiv>
    );
};

const JobAssignmentsTable: React.FC<JobAssignmentsTableProps> = ({
    assignments,
    pagination,
    onSort,
    onPageChange,
    currentSort
}) => {
    const [t] = useTranslation();
    const history = useHistory();

    const handleJobClick = (jobId: string) => {
        history.push(`${JOBS}/${jobId}`);
    };

    const handleRecruiterClick = (recruiterId: string) => {
        history.push(`${RECRUITER}/${recruiterId}`);
    };

    const getActionMenu = (assignment: JobAssignment) => (
        <Menu>
            <Menu.Item
                key="view_job"
                icon={<EyeIcon />}
                onClick={() => handleJobClick(assignment.job_id)}
            >
                {t('VIEW_JOB', 'View Job')}
            </Menu.Item>
            <Menu.Item
                key="view_recruiter"
                icon={<EyeIcon />}
                onClick={() => handleRecruiterClick(assignment.recruiter_id)}
            >
                {t('VIEW_RECRUITER', 'View Recruiter')}
            </Menu.Item>
            <Menu.Item
                key="send_message"
                icon={<MessageIcon />}
                onClick={() => message.info(t('MESSAGE_FEATURE_COMING_SOON', 'Messaging feature coming soon'))}
            >
                {t('SEND_MESSAGE', 'Send Message')}
            </Menu.Item>
            <Menu.Item
                key="edit_assignment"
                icon={<EditIcon />}
                onClick={() => message.info(t('EDIT_FEATURE_COMING_SOON', 'Edit assignment feature coming soon'))}
            >
                {t('EDIT_ASSIGNMENT', 'Edit Assignment')}
            </Menu.Item>
        </Menu>
    );

    const formatTarget = (targetType: string, targetValue: number) => {
        if (!targetType || targetValue === null || targetValue === undefined) {
            return { text: t('NO_TARGET', 'No target'), type: '' };
        }

        let formattedValue = '';
        if (targetType === 'percentage') {
            formattedValue = `${targetValue}%`;
        } else if (targetType === 'fixed') {
            formattedValue = `€${targetValue.toLocaleString()}`;
        } else {
            formattedValue = targetValue.toString();
        }

        return {
            text: formattedValue,
            type: targetType
        };
    };

    return (
        <JobAssignmentsTableDiv>
            {/* Table Header - Using proper styled components */}
            <TableHeader>
                <TableHeaderCell flex="22%">
                    <TableHeaderName
                        name={t('RECRUITER', 'Recruiter')}
                        sortField="recruiter_name"
                        currentSort={currentSort}
                        onSort={onSort}
                    />
                </TableHeaderCell>
                <TableHeaderCell flex="22%">
                    <TableHeaderName
                        name={t('JOB', 'Job')}
                        sortField="job_title"
                        currentSort={currentSort}
                        onSort={onSort}
                    />
                </TableHeaderCell>
                <TableHeaderCell flex="14%">
                    {t('STATUS', 'Status')}
                </TableHeaderCell>
                <TableHeaderCell flex="14%">
                    <TableHeaderName
                        name={t('TARGET', 'Target')}
                        sortField="current_target_value"
                        currentSort={currentSort}
                        onSort={onSort}
                    />
                </TableHeaderCell>
                <TableHeaderCell flex="12%">
                    {t('FEE_TYPE', 'Fee Type')}
                </TableHeaderCell>
                <TableHeaderCell flex="12%">
                    <TableHeaderName
                        name={t('ASSIGNMENT_DATE', 'Assigned')}
                        sortField="assignment_date"
                        currentSort={currentSort}
                        onSort={onSort}
                    />
                </TableHeaderCell>
                <TableHeaderCell flex="4%">
                    {t('ACTIONS', 'Actions')}
                </TableHeaderCell>
            </TableHeader>

            {/* Table Rows */}
            {assignments.map((assignment) => {
                const target = formatTarget(assignment.current_target_type, assignment.current_target_value);

                return (
                    <TableRow key={assignment.assignment_id}>
                        <TableCell flex="22%">
                            <TooltipPrompt title={t('CLICK_TO_VIEW_RECRUITER_PROFILE', 'Click to view recruiter profile')}>
                                <div onClick={() => handleRecruiterClick(assignment.recruiter_id)} style={{ cursor: 'pointer' }}>
                                    <NameImage
                                        title={assignment.recruiter_name}
                                        description={assignment.recruiter_company_name}
                                        image={assignment.recruiter_company_image}
                                        color={getRandomColor()}
                                    />
                                </div>
                            </TooltipPrompt>
                        </TableCell>
                        <TableCell flex="22%">
                            <TooltipPrompt title={t('CLICK_TO_VIEW_JOB_DETAILS', 'Click to view job details')}>
                                <div>
                                    <JobTitle onClick={() => handleJobClick(assignment.job_id)}>
                                        {assignment.job_title}
                                    </JobTitle>
                                    <CompanyName>{assignment.job_company_name}</CompanyName>
                                    {assignment.job_department && (
                                        <CompanyName>{assignment.job_department}</CompanyName>
                                    )}
                                </div>
                            </TooltipPrompt>
                        </TableCell>
                        <TableCell flex="14%">
                            <StatusBadge active={assignment.is_active && assignment.job_status === 'published'}>
                                {assignment.is_active && assignment.job_status === 'published'
                                    ? t('ACTIVE', 'Active')
                                    : t('INACTIVE', 'Inactive')
                                }
                            </StatusBadge>
                            <CompanyName>
                                {t('JOB_STATUS', 'Job')}: {assignment.job_status}
                            </CompanyName>
                        </TableCell>
                        <TableCell flex="14%">
                            <TargetText>{target.text}</TargetText>
                            {target.type && (
                                <TargetType>{target.type}</TargetType>
                            )}
                        </TableCell>
                        <TableCell flex="12%">
                            <FeeType>
                                {assignment.fee_type || t('NOT_SPECIFIED', 'Not specified')}
                            </FeeType>
                        </TableCell>
                        <TableCell flex="12%">
                            <DateText>
                                {new Date(assignment.assignment_date).toLocaleDateString()}
                            </DateText>
                        </TableCell>
                        <TableCell flex="4%">
                            <ActionCell>
                                <Dropdown
                                    overlay={getActionMenu(assignment)}
                                    trigger={['click']}
                                    placement="bottomRight"
                                >
                                    <TableActionButton onClick={(e: React.MouseEvent) => e.preventDefault()}>
                                        <MoreIcon />
                                    </TableActionButton>
                                </Dropdown>
                            </ActionCell>
                        </TableCell>
                    </TableRow>
                );
            })}

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
                <PaginationWrapper>
                    <PaginationButton
                        disabled={!pagination.previous}
                        onClick={() => pagination.previous && onPageChange(pagination.current_page - 1)}
                    >
                        {t('PREVIOUS', 'Previous')}
                    </PaginationButton>

                    {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => (
                        <PaginationButton
                            key={page}
                            active={page === pagination.current_page}
                            onClick={() => onPageChange(page)}
                        >
                            {page}
                        </PaginationButton>
                    ))}

                    <PaginationButton
                        disabled={!pagination.next}
                        onClick={() => pagination.next && onPageChange(pagination.current_page + 1)}
                    >
                        {t('NEXT', 'Next')}
                    </PaginationButton>
                </PaginationWrapper>
            )}
        </JobAssignmentsTableDiv>
    );
};

export default JobAssignmentsTable; 